import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';

// Import types from new models file
import type {
  CourseStep,
  Movement,
  Set as WorkoutSet,
  ActivityType,
  FocusType,
  FocusUnit,
  IntensityFocus,
  IntensityValue,
  HRZoneSpec,
  HRRangeSpec,
  PaceZoneSpec,
  PaceRangeSpec,
  PowerZoneSpec,
  PowerRangeSpec,
} from '../../../store/api/models/workoutModels';

interface WorkoutStructureBuilderProps {
  initialStructure?: CourseStep[];
  onChange?: (structure: CourseStep[]) => void;
  readOnly?: boolean;
}

// Helper functions for time format conversion
const secondsToMMSS = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const mmssToSeconds = (mmss: string): number => {
  const parts = mmss.split(':');
  if (parts.length !== 2) return 0;
  const minutes = parseInt(parts[0]) || 0;
  const seconds = parseInt(parts[1]) || 0;
  return minutes * 60 + seconds;
};

// Helper functions for pace format conversion (decimal minutes <-> MM:SS)
const decimalMinutesToMMSS = (decimalMinutes: number): string => {
  const totalSeconds = Math.floor(decimalMinutes * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const mmssToDecimalMinutes = (mmss: string): number => {
  const parts = mmss.split(':');
  if (parts.length !== 2) return 0;
  const minutes = parseInt(parts[0]) || 0;
  const seconds = parseInt(parts[1]) || 0;
  return minutes + seconds / 60;
};

const mmssToSecondsPerKm = (mmss: string): number => {
  const parts = mmss.split(':');
  if (parts.length !== 2) return NaN;

  const minutes = Number(parts[0]);
  const seconds = Number(parts[1]);

  if (isNaN(minutes) || isNaN(seconds)) return NaN;

  return minutes * 60 + seconds;
};


// Helper functions for unit conversion
const convertDistance = (value: number, fromUnit: FocusUnit, toUnit: FocusUnit): number => {
  // Convert to meters first
  let meters = value;
  if (fromUnit === 'kilometers') meters = value * 1000;
  else if (fromUnit === 'miles') meters = value * 1609.34;

  // Convert from meters to target unit
  if (toUnit === 'meters') return Math.round(meters);
  if (toUnit === 'kilometers') return Math.round(meters / 1000 * 100) / 100;
  if (toUnit === 'miles') return Math.round(meters / 1609.34 * 100) / 100;
  return value;
};

const convertDuration = (value: number, fromUnit: FocusUnit, toUnit: FocusUnit): number => {
  // Convert to seconds first
  let seconds = value;
  if (fromUnit === 'minutes') seconds = value * 60;
  else if (fromUnit === 'hours') seconds = value * 3600;

  // Convert from seconds to target unit
  if (toUnit === 'seconds') return Math.round(seconds);
  if (toUnit === 'minutes') return Math.round(seconds / 60 * 100) / 100;
  if (toUnit === 'hours') return Math.round(seconds / 3600 * 100) / 100;
  return value;
};

// Activity type colors
const getActivityColor = (activityType: ActivityType): string => {
  const colors: Record<ActivityType, string> = {
    warm_up: '#FF9800', // Orange
    run: '#2196F3', // Blue
    jog: '#4CAF50', // Green
    sprint: '#F44336', // Red
    walk: '#9C27B0', // Purple
    rest: '#607D8B', // Blue Grey
    ride: '#00BCD4', // Cyan
    float: '#3F51B5', // Indigo
  };
  return colors[activityType] || '#757575';
};

const WorkoutStructureBuilder: React.FC<WorkoutStructureBuilderProps> = ({
  initialStructure = [],
  onChange,
  readOnly = false,
}) => {
  const [structure, setStructure] = useState<CourseStep[]>(initialStructure);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const [paceTexts, setPaceTexts] = useState<Record<string, { min: string; max: string }>>({});
  
  const [uiIds, setUiIds] = useState<Record<number, number>>({}); 

  const previousStructureRef = React.useRef<CourseStep[] | undefined>(undefined);

  React.useEffect(() => {
    // If we are in "Edit" mode and the data actually loaded (array isn't empty vs empty),
    // or if we just switched templates.
    // We check simple reference equality or deep length comparison to avoid loops.
    if (
      JSON.stringify(initialStructure) !== JSON.stringify(previousStructureRef.current)
    ) {
      setStructure(initialStructure);
      previousStructureRef.current = initialStructure;
      
      // Reset pace texts when structure changes externally to avoid stale data
      setPaceTexts({});
    }
  }, [initialStructure]); 

  const notifyChange = (newStructure: CourseStep[]) => {
    setStructure(newStructure);
    onChange?.(newStructure);
  };

  // --- Helper Constants for Options ---
  
  const DISTANCE_UNITS: FocusUnit[] = ['meters', 'kilometers', 'miles'];
  const DURATION_UNITS: FocusUnit[] = ['seconds', 'minutes', 'hours'];
  
  // --- Creation Helpers ---

  const createDefaultMovement = (): Movement => ({
    activity_type: 'run', // Matched default from Page
    focus_type: 'duration',
    focus_value: 300, // 5:00 in seconds
    focus_unit: 'seconds', // Always seconds for duration (displayed as MM:SS)
    intensity_focus: 'hr',
    intensity_value: { type: 'hr_zone', zone: 2 } as HRZoneSpec,
  });

  // Helper to derive correct units based on focus type
  const getValidUnits = (focusType: FocusType): FocusUnit[] => {
    if (focusType === 'duration') return DURATION_UNITS;
    if (focusType === 'distance') return DISTANCE_UNITS;
    return ['seconds']; // Fallback
  };

  // Helper to get a valid default unit when focus type changes
  const getValidUnit = (focusType: FocusType): FocusUnit => {
    return getValidUnits(focusType)[0];
  };
  

  const addStep = (type: 'movement' | 'set') => {
    const nextOrder = structure.length > 0 
      ? Math.max(...structure.map(s => s.step_order)) + 1 
      : 0;

    const uniqueId = Date.now();

    const newStep: CourseStep = {
      step_type: type,
      step_order: nextOrder,
      content: type === 'set' 
        ? { set_count: 3, movements: [createDefaultMovement()] } as WorkoutSet
        : createDefaultMovement(),
    };
    
    setUiIds(prev => ({ ...prev, [nextOrder]: Date.now() }));

    notifyChange([...structure, newStep]);
  };

  const addMovementToSet = (setStepOrder: number) => {
    const updatedStructure = structure.map(step => {
      if (step.step_order === setStepOrder && step.step_type === 'set') {
        const setContent = step.content as WorkoutSet;
        return {
          ...step,
          content: {
            ...setContent,
            movements: [...setContent.movements, createDefaultMovement()],
          } as WorkoutSet,
        };
      }
      return step;
    });
    notifyChange(updatedStructure);
  };

  // --- Update Handlers ---

  const updateMovement = (stepOrder: number, updates: Partial<Movement>) => {
    const updatedStructure = structure.map(step => {
      if (step.step_order === stepOrder && step.step_type === 'movement') {
        return {
          ...step,
          content: { ...(step.content as Movement), ...updates },
        };
      }
      return step;
    });
    notifyChange(updatedStructure);
  };

  const updateSetMovement = (
    setStepOrder: number, 
    movementIndex: number, 
    updates: Partial<Movement>
  ) => {
    const updatedStructure = structure.map(step => {
      if (step.step_order === setStepOrder && step.step_type === 'set') {
        const setContent = step.content as WorkoutSet;
        const updatedMovements = setContent.movements.map((mov, idx) => 
          idx === movementIndex ? { ...mov, ...updates } : mov
        );
        return {
          ...step,
          content: { ...setContent, movements: updatedMovements } as WorkoutSet,
        };
      }
      return step;
    });
    notifyChange(updatedStructure);
  };

  const updateSetCount = (stepOrder: number, count: number) => {
    const updatedStructure = structure.map(step => {
      if (step.step_order === stepOrder && step.step_type === 'set') {
        return {
          ...step,
          content: { ...(step.content as WorkoutSet), set_count: count },
        };
      }
      return step;
    });
    notifyChange(updatedStructure);
  };

  const deleteStep = (stepOrder: number) => {
    const filtered = structure.filter(step => step.step_order !== stepOrder);
    const reordered = filtered.map((step, idx) => ({ ...step, step_order: idx }));
    notifyChange(reordered);
  };

  const deleteSetMovement = (setStepOrder: number, movementIndex: number) => {
    const updatedStructure = structure.map(step => {
      if (step.step_order === setStepOrder && step.step_type === 'set') {
        const setContent = step.content as WorkoutSet;
        const filtered = setContent.movements.filter((_, idx) => idx !== movementIndex);
        if (filtered.length === 0) return step;
        return {
          ...step,
          content: { ...setContent, movements: filtered } as WorkoutSet,
        };
      }
      return step;
    });
    notifyChange(updatedStructure);
  };

  // --- Drag and Drop ---

  const handleDragStart = (stepOrder: number) => {
    setDraggedItem(stepOrder);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetOrder: number) => {
    if (draggedItem === null || draggedItem === targetOrder) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = structure.findIndex(s => s.step_order === draggedItem);
    const targetIndex = structure.findIndex(s => s.step_order === targetOrder);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    const newStructure = [...structure];
    const [removed] = newStructure.splice(draggedIndex, 1);
    newStructure.splice(targetIndex, 0, removed);

    const reordered = newStructure.map((step, idx) => ({ ...step, step_order: idx }));
    notifyChange(reordered);
    setDraggedItem(null);
  };

  // --- Rendering Logic ---

  const renderMovementEditor = (
    movement: Movement,
    onUpdate: (updates: Partial<Movement>) => void,
    onDelete?: () => void,
    compact: boolean = false,
    movementKey?: string
  ) => {

    // Handler for Focus Type change to auto-correct units
    const handleFocusTypeChange = (e: SelectChangeEvent<FocusType>) => {
      const newFocusType = e.target.value as FocusType;
      // For duration, always use seconds (displayed as MM:SS)
      // For distance, default to kilometers
      const newUnit = newFocusType === 'duration' ? 'seconds' : 'kilometers';
      onUpdate({ focus_type: newFocusType, focus_unit: newUnit });
    };

    // Handler for Unit change with automatic conversion
    const handleUnitChange = (newUnit: FocusUnit) => {
      const oldUnit = movement.focus_unit;
      const oldValue = movement.focus_value;
      let newValue = oldValue;

      if (movement.focus_type === 'distance') {
        newValue = convertDistance(oldValue, oldUnit, newUnit);
      } else if (movement.focus_type === 'duration') {
        newValue = convertDuration(oldValue, oldUnit, newUnit);
      }

      onUpdate({ focus_unit: newUnit, focus_value: newValue });
    };

    // Handler for Intensity Focus change to init correct value structure
    const handleIntensityFocusChange = (e: SelectChangeEvent<IntensityFocus>) => {
      const newType = e.target.value as IntensityFocus;
      let newValue: IntensityValue = null;

      if (newType === 'hr') {
        newValue = { type: 'hr_zone', zone: 2 } as HRZoneSpec; // Default to zone-based HR
      } else if (newType === 'pace') {
        newValue = { type: 'pace_zone', zone: 3 } as PaceZoneSpec; // Default to zone-based pace
      } else if (newType === 'power') {
        newValue = { type: 'power_zone', zone: 3 } as PowerZoneSpec; // Default to zone-based power
      }

      onUpdate({ intensity_focus: newType, intensity_value: newValue });
    };

    const validUnits = getValidUnits(movement.focus_type);
    const activityColor = getActivityColor(movement.activity_type);

    const paceRange = movement.intensity_value as PaceRangeSpec;

    // Determine initial text values safely without useEffect
    // We use the current state if available, otherwise compute from props
    let currentMinText = '';
    let currentMaxText = '';

    if (movementKey && paceTexts[movementKey]) {
        currentMinText = paceTexts[movementKey].min;
        currentMaxText = paceTexts[movementKey].max;
    } else if (paceRange?.type === 'pace_range') {
        // Fallback initialization if state is missing but props exist
        currentMinText = decimalMinutesToMMSS(paceRange.min_pace ?? 0);
        currentMaxText = decimalMinutesToMMSS(paceRange.max_pace ?? 0);
        
        // Sync state immediately if missing, to prevent UI flicker
        if (movementKey) {
            setPaceTexts(prev => ({
                ...prev,
                [movementKey]: { min: currentMinText, max: currentMaxText }
            }));
        }
    }

    const updatePaceText = (field: 'min' | 'max', value: string) => {
      if (!movementKey) return;
      setPaceTexts(prev => ({
        ...prev,
        [movementKey]: {
          ...prev[movementKey],
          [field]: value
        }
      }));
    };

    // REMOVED THE ILLEGAL useEffect HERE

    return (
      <Stack spacing={2.5}>
        {/* Row 1: Activity Type */}
        <FormControl fullWidth size="small">
          <InputLabel>Activity Type</InputLabel>
          <Select
            value={movement.activity_type}
            label="Activity Type"
            disabled={readOnly}
            onChange={(e: SelectChangeEvent) =>
              onUpdate({ activity_type: e.target.value as ActivityType })
            }
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: activityColor,
                borderWidth: 2,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: activityColor,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: activityColor,
              },
            }}
          >
            <MenuItem value="warm_up">Warm Up</MenuItem>
            <MenuItem value="run">Run</MenuItem>
            <MenuItem value="jog">Jog</MenuItem>
            <MenuItem value="sprint">Sprint</MenuItem>
            <MenuItem value="walk">Walk</MenuItem>
            <MenuItem value="rest">Rest</MenuItem>
            <MenuItem value="ride">Ride</MenuItem>
            <MenuItem value="float">Float</MenuItem>
          </Select>
        </FormControl>

        {/* Row 2: Focus Type and Value */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
            Focus Target
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={movement.focus_type === 'duration' ? 6 : 4}>
              <FormControl fullWidth size="small">
                <InputLabel>Focus Type</InputLabel>
                <Select
                  value={movement.focus_type}
                  label="Focus Type"
                  disabled={readOnly}
                  onChange={handleFocusTypeChange}
                >
                  <MenuItem value="duration">Duration</MenuItem>
                  <MenuItem value="distance">Distance</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {movement.focus_type === 'duration' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Duration (MM:SS)"
                  placeholder="5:30"
                  value={secondsToMMSS(movement.focus_value)}
                  disabled={readOnly}
                  onChange={(e) => {
                    // Allow free typing
                    const seconds = mmssToSeconds(e.target.value);
                    if (!isNaN(seconds)) {
                      onUpdate({ focus_value: seconds, focus_unit: 'seconds' });
                    }
                  }}
                  helperText="Format: minutes:seconds"
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Distance"
                    value={movement.focus_value || ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      // Allow free typing, store as-is
                      const value = e.target.value;
                      onUpdate({ focus_value: value === '' ? 0 : Number(value) });
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*\\.?[0-9]*'
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={movement.focus_unit}
                      label="Unit"
                      disabled={readOnly}
                      onChange={(e: SelectChangeEvent) =>
                        handleUnitChange(e.target.value as FocusUnit)
                      }
                    >
                      {validUnits.map((u) => (
                        <MenuItem key={u} value={u}>{u}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Row 3: Intensity */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
            Intensity Target
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: movement.intensity_focus !== 'none' ? 2 : 0 }}>
            <InputLabel>Intensity Type</InputLabel>
            <Select
              value={movement.intensity_focus}
              label="Intensity Type"
              disabled={readOnly}
              onChange={handleIntensityFocusChange}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="hr">Heart Rate</MenuItem>
              <MenuItem value="pace">Pace</MenuItem>
              <MenuItem value="power">Power</MenuItem>
            </Select>
          </FormControl>

          {/* Intensity Value Input (Conditional) */}
          {movement.intensity_focus === 'hr' && (
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel>HR Type</InputLabel>
                <Select
                  value={(movement.intensity_value as HRZoneSpec | HRRangeSpec)?.type === 'hr_zone' ? 'zone' : 'range'}
                  label="HR Type"
                  disabled={readOnly}
                  onChange={(e) => {
                    if (e.target.value === 'zone') {
                      onUpdate({ intensity_value: { type: 'hr_zone', zone: 2 } as HRZoneSpec });
                    } else {
                      onUpdate({ intensity_value: { type: 'hr_range', min_hr: 130, max_hr: 150 } as HRRangeSpec });
                    }
                  }}
                >
                  <MenuItem value="zone">Zone (1-5)</MenuItem>
                  <MenuItem value="range">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {(movement.intensity_value as HRZoneSpec | HRRangeSpec)?.type === 'hr_zone' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Zone</InputLabel>
                  <Select
                    value={(movement.intensity_value as HRZoneSpec)?.zone || 2}
                    label="Zone"
                    disabled={readOnly}
                    onChange={(e) => onUpdate({ intensity_value: { type: 'hr_zone', zone: Number(e.target.value) } as HRZoneSpec })}
                  >
                    {[1, 2, 3, 4, 5].map(z => (
                      <MenuItem key={z} value={z}>Zone {z}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min HR (bpm)"
                    disabled={readOnly}
                    value={(movement.intensity_value as HRRangeSpec)?.min_hr ?? ''}
                    onChange={(e) => {
                      // Allow free typing, store as-is
                      const value = e.target.value;
                      onUpdate({
                        intensity_value: {
                          ...(movement.intensity_value as HRRangeSpec),
                          type: 'hr_range',
                          min_hr: value === '' ? 0 : Number(value)
                        }
                      });
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    helperText="40-220"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Max HR (bpm)"
                    disabled={readOnly}
                    value={(movement.intensity_value as HRRangeSpec)?.max_hr ?? ''}
                    onChange={(e) => {
                      // Allow free typing, store as-is
                      const value = e.target.value;
                      onUpdate({
                        intensity_value: {
                          ...(movement.intensity_value as HRRangeSpec),
                          type: 'hr_range',
                          max_hr: value === '' ? 0 : Number(value)
                        }
                      });
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    helperText="40-220"
                  />
                </Stack>
              )}
            </Stack>
          )}

          {movement.intensity_focus === 'pace' && (
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Pace Type</InputLabel>
                <Select
                  value={(movement.intensity_value as PaceZoneSpec | PaceRangeSpec)?.type === 'pace_zone' ? 'zone' : 'range'}
                  label="Pace Type"
                  disabled={readOnly}
                  onChange={(e) => {
                    if (e.target.value === 'zone') {
                      onUpdate({ intensity_value: { type: 'pace_zone', zone: 3 } as PaceZoneSpec });
                    } else {
                      onUpdate({ intensity_value: { type: 'pace_range', min_pace: 4.5, max_pace: 5.0 } as PaceRangeSpec });
                    }
                  }}
                >
                  <MenuItem value="zone">Zone (1-5)</MenuItem>
                  <MenuItem value="range">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {(movement.intensity_value as PaceZoneSpec | PaceRangeSpec)?.type === 'pace_zone' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Zone</InputLabel>
                  <Select
                    value={(movement.intensity_value as PaceZoneSpec)?.zone || 3}
                    label="Zone"
                    disabled={readOnly}
                    onChange={(e) => onUpdate({ intensity_value: { type: 'pace_zone', zone: Number(e.target.value) } as PaceZoneSpec })}
                  >
                    {[1, 2, 3, 4, 5].map(z => (
                      <MenuItem key={z} value={z}>Zone {z}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Min Pace (MM:SS/km)"
                      placeholder="4:30"
                      disabled={readOnly}
                      value={currentMinText}
                      onChange={(e) => {
                        updatePaceText('min', e.target.value);
                      }}
                      onBlur={() => {
                        const decimalMinutes = mmssToSecondsPerKm(currentMinText);
                        if (!isNaN(decimalMinutes)) {
                          onUpdate({
                            intensity_value: {
                              ...paceRange,
                              type: 'pace_range',
                              min_pace: decimalMinutes
                            }
                          });
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Max Pace (MM:SS/km)"
                      placeholder="5:00"
                      disabled={readOnly}
                      value={currentMaxText}
                      onChange={(e) => {
                        updatePaceText('max', e.target.value);
                      }}
                      onBlur={() => {
                        const decimalMinutes = mmssToSecondsPerKm(currentMaxText);
                        if (!isNaN(decimalMinutes)) {
                          onUpdate({
                            intensity_value: {
                              ...paceRange,
                              type: 'pace_range',
                              max_pace: decimalMinutes
                            }
                          });
                        }
                      }}
                    />
                </Stack>
              )}
            </Stack>
          )}

          {movement.intensity_focus === 'power' && (
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Power Type</InputLabel>
                <Select
                  value={(movement.intensity_value as PowerZoneSpec | PowerRangeSpec)?.type === 'power_zone' ? 'zone' : 'range'}
                  label="Power Type"
                  disabled={readOnly}
                  onChange={(e) => {
                    if (e.target.value === 'zone') {
                      onUpdate({ intensity_value: { type: 'power_zone', zone: 3 } as PowerZoneSpec });
                    } else {
                      onUpdate({ intensity_value: { type: 'power_range', min_watts: 200, max_watts: 250 } as PowerRangeSpec });
                    }
                  }}
                >
                  <MenuItem value="zone">Zone (1-5)</MenuItem>
                  <MenuItem value="range">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {(movement.intensity_value as PowerZoneSpec | PowerRangeSpec)?.type === 'power_zone' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Zone</InputLabel>
                  <Select
                    value={(movement.intensity_value as PowerZoneSpec)?.zone || 3}
                    label="Zone"
                    disabled={readOnly}
                    onChange={(e) => onUpdate({ intensity_value: { type: 'power_zone', zone: Number(e.target.value) } as PowerZoneSpec })}
                  >
                    {[1, 2, 3, 4, 5].map(z => (
                      <MenuItem key={z} value={z}>Zone {z}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Power (watts)"
                    disabled={readOnly}
                    value={(movement.intensity_value as PowerRangeSpec)?.min_watts ?? ''}
                    onChange={(e) => {
                      // Allow free typing, store as-is
                      const value = e.target.value;
                      onUpdate({
                        intensity_value: {
                          ...(movement.intensity_value as PowerRangeSpec),
                          type: 'power_range',
                          min_watts: value === '' ? 0 : Number(value)
                        }
                      });
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Max Power (watts)"
                    disabled={readOnly}
                    value={(movement.intensity_value as PowerRangeSpec)?.max_watts ?? ''}
                    onChange={(e) => {
                      // Allow free typing, store as-is
                      const value = e.target.value;
                      onUpdate({
                        intensity_value: {
                          ...(movement.intensity_value as PowerRangeSpec),
                          type: 'power_range',
                          max_watts: value === '' ? 0 : Number(value)
                        }
                      });
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                  />
                </Stack>
              )}
            </Stack>
          )}

        </Box>

        {/* Delete button for movements in sets */}
        {onDelete && !readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              sx={{
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'error.light',
                  color: 'white',
                },
              }}
            >
              Remove
            </Button>
          </Box>
        )}
      </Stack>
    );
  };

  const renderSetStep = (step: CourseStep) => {
    const stepId = uiIds[step.step_order];
    const expanded = expandedIds[stepId] ?? true;
    const setContent = step.content as WorkoutSet;
    const toggleExpanded = () => {
      setExpandedIds(prev => ({ ...prev, [stepId]: !expanded }));
    };

    // Get first movement's color for the set
    const firstMovementColor = setContent.movements.length > 0
      ? getActivityColor(setContent.movements[0].activity_type)
      : '#9C27B0';

    return (
      <Paper
        key={step.step_order}
        elevation={draggedItem === step.step_order ? 8 : 3}
        draggable={!readOnly}
        onDragStart={() => handleDragStart(step.step_order)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(step.step_order)}
        sx={{
          p: 2.5,
          mb: 2,
          borderLeft: '6px solid',
          borderLeftColor: firstMovementColor,
          borderRadius: 2,
          cursor: readOnly ? 'default' : 'move',
          opacity: draggedItem === step.step_order ? 0.5 : 1,
          transition: 'all 0.3s ease',
          background: `linear-gradient(135deg, ${firstMovementColor}08 0%, transparent 100%)`,
          '&:hover': {
            transform: readOnly ? 'none' : 'translateY(-2px)',
            boxShadow: 6,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          {!readOnly && <DragIcon sx={{ color: firstMovementColor, fontSize: 28 }} />}

          <Chip
            label="SET"
            size="small"
            sx={{
              bgcolor: firstMovementColor,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />

          <TextField
            size="small"
            label="Repetitions"
            value={setContent.set_count}
            disabled={readOnly}
            onChange={(e) => {
              // Allow free typing, store as-is
              const value = e.target.value;
              updateSetCount(step.step_order, value === '' ? 1 : Number(value));
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            sx={{ width: 120 }}
          />

          <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }}>
            {setContent.movements.length} movement{setContent.movements.length !== 1 ? 's' : ''}
          </Typography>

          <IconButton
            size="small"
            onClick={() => toggleExpanded()}
            sx={{
              bgcolor: expanded ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>

          {!readOnly && (
            <IconButton
              color="error"
              size="small"
              onClick={() => deleteStep(step.step_order)}
              sx={{
                '&:hover': {
                  bgcolor: 'error.light',
                  color: 'white',
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        <Collapse in={expanded}>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            {setContent.movements.map((movement, idx) => {
              const movementColor = getActivityColor(movement.activity_type);
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 2.5,
                    border: '2px solid',
                    borderColor: movementColor + '40',
                    borderRadius: 2,
                    backgroundColor: movementColor + '08',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: movementColor,
                      boxShadow: `0 4px 12px ${movementColor}30`,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: movementColor,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: movementColor,
                      }}
                    />
                    Movement {idx + 1}
                  </Typography>
                  {renderMovementEditor(
                    movement,
                    (updates) => updateSetMovement(step.step_order, idx, updates),
                    setContent.movements.length > 1
                      ? () => deleteSetMovement(step.step_order, idx)
                      : undefined,
                    true,
                    `set-${step.step_order}-movement-${idx}`
                  )}
                </Box>
              );
            })}

            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => addMovementToSet(step.step_order)}
              >
                Add Movement to Set
              </Button>
            )}
          </Stack>
        </Collapse>
      </Paper>
    );
  };

  const renderMovementStep = (step: CourseStep) => {
    const movement = step.content as Movement;
    const movementColor = getActivityColor(movement.activity_type);

    return (
      <Paper
        key={step.step_order}
        elevation={draggedItem === step.step_order ? 8 : 3}
        draggable={!readOnly}
        onDragStart={() => handleDragStart(step.step_order)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(step.step_order)}
        sx={{
          p: 2.5,
          mb: 2,
          borderLeft: '6px solid',
          borderLeftColor: movementColor,
          borderRadius: 2,
          cursor: readOnly ? 'default' : 'move',
          opacity: draggedItem === step.step_order ? 0.5 : 1,
          transition: 'all 0.3s ease',
          background: `linear-gradient(135deg, ${movementColor}08 0%, transparent 100%)`,
          '&:hover': {
            transform: readOnly ? 'none' : 'translateY(-2px)',
            boxShadow: 6,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          {!readOnly && <DragIcon sx={{ color: movementColor, fontSize: 28 }} />}
          <Chip
            label="MOVEMENT"
            size="small"
            sx={{
              bgcolor: movementColor,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
          <Box sx={{ flex: 1 }} />
          {!readOnly && (
            <IconButton
              color="error"
              size="small"
              onClick={() => deleteStep(step.step_order)}
              sx={{
                '&:hover': {
                  bgcolor: 'error.light',
                  color: 'white',
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        {renderMovementEditor(
          movement,
          (updates) => updateMovement(step.step_order, updates),
          undefined,
          false,
          `movement-${step.step_order}`
        )}
      </Paper>
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h5"
          mb={3}
          fontWeight="bold"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Workout Structure
        </Typography>

        {structure.length === 0 && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 2,
              borderLeft: '4px solid',
              borderLeftColor: 'info.main',
            }}
          >
            No steps added yet. Click the buttons below to start building your workout.
          </Alert>
        )}

        <Box sx={{ minHeight: 200, mb: 3 }}>
          {structure.map(step =>
            step.step_type === 'set' ? renderSetStep(step) : renderMovementStep(step)
          )}
        </Box>

        {!readOnly && (
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => addStep('movement')}
              sx={{
                bgcolor: '#667eea',
                '&:hover': {
                  bgcolor: '#5568d3',
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                px: 3,
              }}
            >
              Add Movement
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => addStep('set')}
              sx={{
                bgcolor: '#764ba2',
                '&:hover': {
                  bgcolor: '#633991',
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                px: 3,
              }}
            >
              Add Set (Repeat)
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkoutStructureBuilder;